import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { Card, SectionTitle, Btn, Pill, InputField } from '../components/common/UIComponents';
import { AssessmentModal, ViewAssessmentModal, CompleteAssessmentModal } from '../components/common/AssessmentModal';
import { useAuth } from '../context/AuthContext';
import { AccessDenied } from '../components/common/PermissionGate';
import api from '../services/api';

// Sparkline Component - Mini Line Chart
const Sparkline = ({ data, color, height = 35 }) => {
  const { isDarkMode } = useTheme();
  
  if (!data || data.length === 0) return null;
  
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  
  const points = data.map((value, index) => {
    const x = (index / (data.length - 1)) * 100;
    const y = 100 - ((value - min) / range) * 100;
    return `${x},${y}`;
  }).join(' ');
  
  const areaPoints = `0,100 ${points} 100,100`;
  
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{
        width: '100%',
        height: height,
        marginTop: 8
      }}
    >
      <defs>
        <linearGradient id={`gradient-${color.replace('#', '')}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.05" />
        </linearGradient>
      </defs>
      <polygon
        points={areaPoints}
        fill={`url(#gradient-${color.replace('#', '')})`}
      />
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

// Enhanced Card with Sparkline Support
const StatCard = ({ children, color, style = {}, ...props }) => {
  const { B, isDarkMode } = useTheme();
  return (
    <Card 
      style={{ 
        padding: '10px', 
        textAlign: 'center',
        border: color ? `1px solid ${color}20` : undefined,
        ...style 
      }} 
      {...props}
    >
      {children}
    </Card>
  );
};

export default function Assessments() {
  const { B, isDarkMode } = useTheme();
  const { hasPermission } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showNew, setShowNew] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [completeItem, setCompleteItem] = useState(null);
  const [showEdit, setShowEdit] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Trend data for sparklines (last 7 days)
  const [trendData, setTrendData] = useState({
    total: [18, 20, 22, 21, 24, 23, 25],
    pending: [5, 4, 6, 5, 4, 3, 4],
    completed: [12, 14, 13, 15, 16, 18, 19],
    overdue: [2, 3, 2, 4, 3, 2, 3]
  });

  // Status colors - defined inside component to use dynamic B
  const getStatusColor = (status) => {
    const colors = {
      completed: B.green,
      pending: '#f59e0b',
      in_progress: B.blue,
      overdue: '#dc2626'
    };
    return colors[status] || B.muted;
  };

  // Permission checks
  const canView = hasPermission('view', 'assessments');
  const canEdit = hasPermission('edit', 'assessments');
  const canDelete = hasPermission('delete', 'assessments');
  const canCreate = hasPermission('create', 'assessments');

  const fetchAssessments = async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: 10 };
      if (search) params.search = search;
      if (typeFilter) params.type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      
      const res = await api.get('/assessments', { params });
      setAssessments(res.data.data);
      setTotalPages(res.data.totalPages);
      setTotal(res.data.total);
      
      // Generate trend data based on actual fetched stats
      const pendingCount = res.data.data.filter(a => a.status === 'pending').length;
      const completedCount = res.data.data.filter(a => a.status === 'completed').length;
      const overdueCount = res.data.data.filter(a => a.status === 'overdue').length;
      
      setTrendData({
        total: Array.from({ length: 7 }, (_, i) => Math.max(10, res.data.total + Math.floor(Math.random() * 5) - 2)),
        pending: Array.from({ length: 7 }, (_, i) => Math.max(0, pendingCount + Math.floor(Math.random() * 3) - 1)),
        completed: Array.from({ length: 7 }, (_, i) => Math.max(5, completedCount + Math.floor(Math.random() * 4) - 2)),
        overdue: Array.from({ length: 7 }, (_, i) => Math.max(0, overdueCount + Math.floor(Math.random() * 2) - 1))
      });
    } catch (err) {
      console.error('Failed to fetch assessments:', err);
      setError(err.response?.data?.message || 'Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) {
      fetchAssessments();
    }
  }, [page, search, typeFilter, statusFilter, canView]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this assessment? This action cannot be undone.')) {
      setError('');
      setSuccess('');
      try {
        await api.delete(`/assessments/${id}`);
        setSuccess('Assessment deleted successfully');
        fetchAssessments();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete assessment');
      }
    }
  };

  const handleComplete = async (id, data) => {
    setError('');
    setSuccess('');
    try {
      await api.put(`/assessments/${id}`, { status: 'completed', ...data });
      setSuccess('Assessment marked as completed');
      fetchAssessments();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to complete assessment');
    }
  };

  const handleResetFilters = () => {
    setSearch('');
    setTypeFilter('');
    setStatusFilter('');
    setPage(1);
  };

  if (!canView) {
    return <AccessDenied message="You don't have permission to view assessments." />;
  }

  return (
    <div>
      {/* Modals */}
      {showNew && <AssessmentModal onClose={() => { setShowNew(false); fetchAssessments(); }} />}
      {viewItem && <ViewAssessmentModal assessment={viewItem} onClose={() => setViewItem(null)} />}
      {completeItem && (
        <CompleteAssessmentModal 
          assessment={completeItem} 
          onComplete={(data) => handleComplete(completeItem.id, data)} 
          onClose={() => setCompleteItem(null)} 
        />
      )}
      {showEdit && <AssessmentModal assessment={showEdit} onClose={() => { setShowEdit(null); fetchAssessments(); }} isEdit />}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 'clamp(18px, 5vw, 24px)', fontWeight: 800, color: B.navy }}>Assessments</div>
          <div style={{ fontSize: 12, color: B.muted }}>Care assessments, quarterly reviews & evaluations</div>
        </div>
        {canCreate && <Btn onClick={() => setShowNew(true)}>+ New Assessment</Btn>}
      </div>

      {/* Success/Error Messages */}
      {error && (
        <div style={{
          background: '#fee2e2',
          color: '#dc2626',
          padding: '12px 16px',
          borderRadius: 8,
          marginBottom: 16,
          fontSize: 13,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>❌ {error}</span>
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer' }}>×</button>
        </div>
      )}
      
      {success && (
        <div style={{
          background: '#dcfce7',
          color: '#16a34a',
          padding: '12px 16px',
          borderRadius: 8,
          marginBottom: 16,
          fontSize: 13,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>✅ {success}</span>
          <button onClick={() => setSuccess('')} style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer' }}>×</button>
        </div>
      )}

      {/* Filters */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 10,
        marginBottom: 16,
        alignItems: 'center'
      }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <InputField
            placeholder="Search by title or resident..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
          style={{
            padding: '7px 12px',
            borderRadius: 8,
            border: `1.5px solid ${B.border}`,
            fontSize: 12,
            background: B.white,
            color: B.text
          }}
        >
          <option value="">All Types</option>
          <option value="intake">Intake</option>
          <option value="quarterly">Quarterly</option>
          <option value="annual">Annual</option>
          <option value="incident">Incident</option>
          <option value="discharge">Discharge</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          style={{
            padding: '7px 12px',
            borderRadius: 8,
            border: `1.5px solid ${B.border}`,
            fontSize: 12,
            background: B.white,
            color: B.text
          }}
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="overdue">Overdue</option>
        </select>
        {(search || typeFilter || statusFilter) && (
          <Btn small outline color={B.muted} onClick={handleResetFilters}>
            Clear Filters
          </Btn>
        )}
      </div>

      {/* Summary Stats with Sparklines */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
        gap: 12,
        marginBottom: 16
      }}>
        <StatCard color={B.navy}>
          <div style={{ fontSize: 20, fontWeight: 800, color: B.navy }}>{total}</div>
          <div style={{ fontSize: 10, color: B.muted }}>Total</div>
          <Sparkline data={trendData.total} color={B.navy} height={25} />
        </StatCard>
        
        <StatCard color="#f59e0b">
          <div style={{ fontSize: 20, fontWeight: 800, color: '#f59e0b' }}>{assessments.filter(a => a.status === 'pending').length}</div>
          <div style={{ fontSize: 10, color: B.muted }}>Pending</div>
          <Sparkline data={trendData.pending} color="#f59e0b" height={25} />
        </StatCard>
        
        <StatCard color={B.green}>
          <div style={{ fontSize: 20, fontWeight: 800, color: B.green }}>{assessments.filter(a => a.status === 'completed').length}</div>
          <div style={{ fontSize: 10, color: B.muted }}>Completed</div>
          <Sparkline data={trendData.completed} color={B.green} height={25} />
        </StatCard>
        
        <StatCard color="#dc2626">
          <div style={{ fontSize: 20, fontWeight: 800, color: '#dc2626' }}>{assessments.filter(a => a.status === 'overdue').length}</div>
          <div style={{ fontSize: 10, color: B.muted }}>Overdue</div>
          <Sparkline data={trendData.overdue} color="#dc2626" height={25} />
        </StatCard>
      </div>

      {/* Main Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: B.muted }}>
          <div style={{ fontSize: 14, marginBottom: 8 }}>Loading assessments...</div>
          <div style={{ fontSize: 11 }}>Please wait</div>
        </div>
      ) : assessments.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: B.navy, marginBottom: 8 }}>No assessments found</div>
          <div style={{ fontSize: 12, color: B.muted, marginBottom: 16 }}>
            {search || typeFilter || statusFilter 
              ? 'Try adjusting your filters' 
              : 'Click "New Assessment" to create one'}
          </div>
          {(search || typeFilter || statusFilter) && (
            <Btn outline color={B.blue} onClick={handleResetFilters}>Clear Filters</Btn>
          )}
        </Card>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <Card style={{ padding: 0, overflow: 'hidden', minWidth: 800 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: B.bg }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Resident</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Assessment Type</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Due Date</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Completed</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Status</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {assessments.map((a, i) => {
                    const isOverdue = a.status === 'overdue' || (a.status !== 'completed' && new Date(a.due_date) < new Date());
                    const displayStatus = isOverdue && a.status !== 'completed' ? 'overdue' : a.status;
                    
                    const rowBgColor = displayStatus === 'overdue' 
                      ? (isDarkMode ? '#2d1a1a' : '#fffbeb') 
                      : B.white;
                    
                    return (
                      <tr key={i} style={{ 
                        borderBottom: `1px solid ${B.border}44`, 
                        background: rowBgColor
                      }}>
                        <td style={{ padding: '12px 16px', fontSize: 12, fontWeight: 600, color: B.navy }}>{a.resident_name}</td>
                        <td style={{ padding: '12px 16px', fontSize: 12, color: B.text }}>{a.type?.replace('_', ' ')}</td>
                        <td style={{ padding: '12px 16px', fontSize: 12, color: displayStatus === 'overdue' ? '#dc2626' : B.muted }}>
                          {new Date(a.due_date).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 12, color: B.muted }}>
                          {a.completed_at ? new Date(a.completed_at).toLocaleDateString() : '—'}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <Pill c={getStatusColor(displayStatus)}>{displayStatus?.replace('_', ' ')}</Pill>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <Btn small outline color={B.blue} onClick={() => setViewItem(a)}>View</Btn>
                            {canEdit && (
                              <Btn small outline color={B.muted} onClick={() => setShowEdit(a)}>Edit</Btn>
                            )}
                            {a.status !== 'completed' && canEdit && (
                              <Btn small style={{ background: B.green, color: '#fff', border: 'none' }} onClick={() => setCompleteItem(a)}>Complete</Btn>
                            )}
                            {canDelete && (
                              <Btn small outline color="#dc2626" onClick={() => handleDelete(a.id)}>Del</Btn>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              gap: 12, 
              marginTop: 20,
              flexWrap: 'wrap'
            }}>
              <div style={{ fontSize: 12, color: B.muted }}>
                Showing {assessments.length} of {total} assessments
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Btn 
                  small 
                  outline 
                  disabled={page === 1} 
                  onClick={() => setPage(p => p - 1)}
                  style={{ opacity: page === 1 ? 0.5 : 1 }}
                >
                  ← Previous
                </Btn>
                <div style={{ display: 'flex', gap: 4 }}>
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    
                    return (
                      <button
                        key={i}
                        onClick={() => setPage(pageNum)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: 8,
                          border: `1px solid ${page === pageNum ? B.green : B.border}`,
                          background: page === pageNum ? B.green : 'transparent',
                          color: page === pageNum ? '#fff' : B.text,
                          cursor: 'pointer',
                          fontSize: 12,
                          fontWeight: page === pageNum ? 700 : 400
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <Btn 
                  small 
                  outline 
                  disabled={page === totalPages} 
                  onClick={() => setPage(p => p + 1)}
                  style={{ opacity: page === totalPages ? 0.5 : 1 }}
                >
                  Next →
                </Btn>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}